import * as React from "react";

import { ContextMenu, MenuOptions } from "@/shared/components/ContextMenu";
import {
  useUpdateUserConfig,
  useUserConfig,
} from "@/shared/domain/config/hooks";
import {
  createEmptyLayout,
  isTemporaryLayout,
  LayoutRecord,
} from "@/shared/domain/layouts";
import { LayoutAccessibility } from "@/shared/domain/layouts/LayoutRecord";
import { usePopupState } from "@/shared/hooks";
import { useVizState } from "@/shared/state/visualization";

import {
  useActiveLayout,
  useWorkspaceSnapshot,
} from "../../WorkspaceCtx/hooks";

import { CreateLayoutFormModal } from "./CreateLayoutFormModal";
import { DeleteLayoutConfirmation } from "./DeleteLayoutConfirmation";
import { RenameLayoutModal } from "./RenameLayoutModal";
import { SaveLayoutConfirmation } from "./SaveLayoutConfirmation";
import { SelectLayoutMenu } from "./SelectLayoutMenu";
import { ShareLayoutConfirmation } from "./ShareLayoutConfirmation";

interface SelectLayoutProps {
  anchorEl: HTMLElement | null;
  isOpen: boolean;
  onClose: () => void;
}

export function SelectLayout(props: SelectLayoutProps) {
  const layoutMenu = {
    anchor: props.anchorEl,
    close: props.onClose,
    isOpen: props.isOpen,
  };

  const { contextMenu, modalState, handlers, toContextOptions } =
    useLayoutMenuController(layoutMenu);

  return (
    <>
      <SelectLayoutMenu
        anchorEl={layoutMenu.anchor}
        isOpen={layoutMenu.isOpen}
        onClose={layoutMenu.close}
        onOpenContextMenu={handlers.onOpenContextMenu}
        onSaveLayout={handlers.onSaveLayout}
        onSelectLayout={handlers.onSelectLayout}
      />

      <ContextMenu
        anchorEl={contextMenu.anchor}
        isOpen={contextMenu.isOpen}
        onClose={contextMenu.close}
        options={
          contextMenu.metadata ? toContextOptions(contextMenu.metadata) : []
        }
      />

      <CreateLayoutFormModal
        isOpen={modalState.type === "create"}
        onClose={handlers.onCloseModal}
        onSuccess={handlers.onCreateLayoutSuccess}
      />

      <DeleteLayoutConfirmation
        layout={modalState.layout}
        isOpen={modalState.type === "delete"}
        onClose={handlers.onCloseModal}
        onExited={handlers.onModalExited}
        onSuccess={handlers.onDeleteLayoutSuccess}
      />

      <RenameLayoutModal
        layout={modalState.layout}
        isOpen={modalState.type === "rename"}
        onClose={handlers.onCloseModal}
        onExited={handlers.onModalExited}
        onSuccess={handlers.onRenameLayoutSuccess}
      />

      <SaveLayoutConfirmation
        isOpen={modalState.type === "save"}
        layout={modalState.layout}
        onClose={handlers.onCloseModal}
        onExited={handlers.onModalExited}
        onAnswered={handlers.onAnsweredSaveLayout}
      />

      <ShareLayoutConfirmation
        layout={modalState.layout}
        isOpen={modalState.type === "share"}
        onClose={handlers.onCloseModal}
        onExited={handlers.onModalExited}
      />
    </>
  );
}

type ModalState = {
  type: "create" | "delete" | "save" | "share" | "rename" | null;
  layout: LayoutRecord | null;
};

function useLayoutMenuController(layoutMenu: { close: () => void }) {
  const contextMenu = usePopupState<LayoutRecord>();
  const [modalState, setModalState] = React.useState<ModalState>({
    type: null,
    layout: null,
  });
  const [activeLayout, setActiveLayout] = useActiveLayout();
  const vizState = useVizState();
  const [_, setWorkspaceSnapshot] = useWorkspaceSnapshot();

  const defaultLayoutQuery = useUserConfig("user_default_layout_id");

  const setDefaultLayoutMutation = useUpdateUserConfig();

  const handleOpenContextMenu = (
    event: React.MouseEvent<HTMLElement>,
    layout: LayoutRecord,
  ) => {
    // Prevents firing the onClick event for the menu item in the original menu when opening the context menu
    event.stopPropagation();

    contextMenu.open({
      anchorEl: event.currentTarget.parentElement,
      metadata: layout,
    });
  };

  const handleCreateLayout = () => {
    setModalState({ type: "create", layout: null });
    layoutMenu.close();
  };

  const handleSaveLayout = () => {
    if (!activeLayout || isTemporaryLayout(activeLayout)) {
      handleCreateLayout();
    } else {
      setModalState({ type: "save", layout: activeLayout });
      layoutMenu.close();
    }
  };

  const handleSelectLayout = (layout: LayoutRecord) => {
    setActiveLayout(layout);
    layoutMenu.close();
  };

  const handleShareLayout = (layout: LayoutRecord) => {
    setModalState({ type: "share", layout });
    contextMenu.close();
  };

  const handleRenameLayout = (layout: LayoutRecord) => {
    setModalState({ type: "rename", layout });
    contextMenu.close();
  };

  const handleDeleteLayout = (layout: LayoutRecord) => {
    setModalState({ type: "delete", layout });
    contextMenu.close();
  };

  const handleDeleteLayoutSuccess = (layout: LayoutRecord) => {
    // If the deleted layout was the active layout, we unselect it by setting active layout to the default layout
    // but set the skipReload flag to true so we keep the deleted layout's state
    if (activeLayout?.layout_id === layout.layout_id) {
      setActiveLayout(createEmptyLayout(), { skipReload: true });
    }
    handleCloseModal();
  };

  const handleCloseModal = () => {
    setModalState((state) => ({
      ...state,
      type: null,
    }));
  };

  const handleModalExited = () => {
    setModalState((state) => ({
      ...state,
      layout: null,
    }));
  };

  const handleCreateLayoutSuccess = (newLayout: LayoutRecord) => {
    setActiveLayout(newLayout, { skipReload: true });
    setWorkspaceSnapshot({
      panels: vizState.panels,
      layout: vizState.layout,
    });
    handleCloseModal();
  };

  const handleOnAnsweredSaveLayout = (
    answer: { shouldOverwrite: boolean },
    newLayout?: LayoutRecord,
  ) => {
    if (answer.shouldOverwrite && newLayout) {
      handleCloseModal();
      setActiveLayout(newLayout, { skipReload: true });
      setWorkspaceSnapshot({
        panels: vizState.panels,
        layout: vizState.layout,
      });
    } else {
      handleCreateLayout();
    }
  };

  const handleRenameLayoutSuccess = (updatedLayout: LayoutRecord) => {
    // If the renamed layout is the active layout, update the active layout reference
    if (activeLayout?.layout_id === updatedLayout.layout_id) {
      setActiveLayout(updatedLayout, { skipReload: true });
    }
    handleCloseModal();
  };

  const handleSetAsDefault = (layout: LayoutRecord) => {
    setDefaultLayoutMutation.mutate({
      key: "user_default_layout_id",
      value: layout.layout_id,
    });
    contextMenu.close();
  };

  const handleRemoveAsDefault = () => {
    setDefaultLayoutMutation.mutate({
      key: "user_default_layout_id",
      value: "",
    });
    contextMenu.close();
  };

  const toContextOptions = (layout: LayoutRecord): MenuOptions[] => {
    const options = [];
    const isCurrentDefault = defaultLayoutQuery.data === layout.layout_id;

    if (isCurrentDefault) {
      options.push({
        eventName: "RemoveAsDefaultLayoutOptionClicked" as const,
        eventProperties: {
          layoutId: layout.layout_id,
          layoutName: layout.name,
        },
        label: "Remove as default",
        onClick: () => handleRemoveAsDefault(),
        style: {
          typography: "body2",
        },
      });
    } else {
      options.push({
        eventName: "SetAsDefaultLayoutOptionClicked" as const,
        eventProperties: {
          layoutId: layout.layout_id,
          layoutName: layout.name,
        },
        label: "Set as default",
        onClick: () => handleSetAsDefault(layout),
        style: {
          typography: "body2",
        },
      });
    }

    if (layout.accessibility === LayoutAccessibility.User) {
      options.push({
        eventName: "ShareLayoutOptionClicked" as const,
        eventProperties: {
          layoutId: layout.layout_id,
          layoutName: layout.name,
        },
        label: "Share with team",
        onClick: () => handleShareLayout(layout),
        style: {
          typography: "body2",
        },
      });
    }

    options.push({
      eventName: "RenameLayoutOptionClicked" as const,
      eventProperties: {
        layoutId: layout.layout_id,
        layoutName: layout.name,
      },
      label: "Rename",
      onClick: () => handleRenameLayout(layout),
      style: {
        typography: "body2",
      },
    });

    options.push({
      eventName: "DeleteLayoutOptionClicked" as const,
      eventProperties: {
        layoutId: layout.layout_id,
        layoutName: layout.name,
      },
      label: "Delete",
      onClick: () => handleDeleteLayout(layout),
      style: {
        typography: "body2",
        color: "error.main",
      },
    });

    return options;
  };

  return {
    contextMenu,
    modalState,
    handlers: {
      onCloseModal: handleCloseModal,
      onCreateLayout: handleCreateLayout,
      onCreateLayoutSuccess: handleCreateLayoutSuccess,
      onDeleteLayoutSuccess: handleDeleteLayoutSuccess,
      onModalExited: handleModalExited,
      onOpenContextMenu: handleOpenContextMenu,
      onRenameLayoutSuccess: handleRenameLayoutSuccess,
      onSaveLayout: handleSaveLayout,
      onAnsweredSaveLayout: handleOnAnsweredSaveLayout,
      onSelectLayout: handleSelectLayout,
    },
    toContextOptions,
  };
}
